"use client";

import { Button } from "@punchup/ui-common/shadcn/ui/button";
import {
  ModalOrDrawer,
  PunchupButton,
  SearchInput,
} from "@punchup/ui-common/components/common";
import { Skeleton } from "@punchup/ui-common/shadcn/ui/skeleton";
import { useEffect, useMemo, useState } from "react";

import ShowSelectRow from "./ShowSelectRow";
import { VenueCarouselItem } from "@punchup/data/models";
import { cn } from "@punchup/utils/cn";
import { useVenuePageShows } from "../../../hooks/venuePages/useVenuePageShows";

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  venuePageId: string;
  venueSlug: string;
  existingItems: VenueCarouselItem[];
  onAddShows: (showIds: string[]) => void;
  isAdding: boolean;
}

export default function SelectShowsModal({
  isOpen,
  onOpenChange,
  venuePageId,
  venueSlug,
  existingItems,
  onAddShows,
  isAdding,
}: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedShowIds, setSelectedShowIds] = useState<Set<string>>(
    new Set()
  );
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSelectedShowIds(new Set());
      setHasScrolled(false);
    }
  }, [isOpen]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const isScrolled = e.currentTarget.scrollTop > 0;
    if (isScrolled !== hasScrolled) setHasScrolled(isScrolled);
  };

  const { shows, isLoading, fetchMoreShows, isLoadingMore, hasMore } =
    useVenuePageShows({
      venuePageId,
      venueSlug,
      enabled: isOpen,
    });

  const existingShowIds = useMemo(() => {
    const showIds: string[] = [];
    for (const item of existingItems) {
      if (item.type === "show") {
        showIds.push(item.show.id);
      }
    }
    return new Set(showIds);
  }, [existingItems]);

  const filteredShows = useMemo(() => {
    if (!searchQuery.trim()) return shows;

    const query = searchQuery.toLowerCase();
    return shows.filter((show) => {
      const title = show.title?.toLowerCase() || "";
      return title.includes(query);
    });
  }, [shows, searchQuery]);

  const handleToggle = (showId: string) => {
    setSelectedShowIds((prev) => {
      const next = new Set(prev);
      if (next.has(showId)) {
        next.delete(showId);
      } else {
        next.add(showId);
      }
      return next;
    });
  };

  const handleAdd = () => {
    const showIds = Array.from(selectedShowIds);
    onAddShows(showIds);
  };

  const handleClose = () => {
    setSearchQuery("");
    setSelectedShowIds(new Set());
    onOpenChange(false);
  };

  const selectedCount = selectedShowIds.size;

  return (
    <ModalOrDrawer isOpen={isOpen} onOpenChange={handleClose} className="p-0">
      <div className="flex flex-col max-h-[80vh] overflow-hidden">
        <div className="flex flex-col gap-y-4 flex-1 min-h-0 overflow-hidden">
          <div className="flex flex-col gap-y-4 px-5 pt-2 md:pt-5">
            <div className="flex flex-col gap-y-1">
              <h2 className="text-lg font-semibold text-primary-blue">
                Select shows
              </h2>
              <p className="text-sm text-secondary-blue">
                Choose shows to add to your carousel
              </p>
            </div>

            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
              placeholder="Search for shows..."
            />
          </div>

          <div
            className={cn(
              "flex-1 overflow-y-auto min-h-0 max-h-[50vh] px-5 pb-5 transition-all duration-150 ease-in-out",
              hasScrolled && "border-t border-neutral-200"
            )}
            onScroll={handleScroll}
          >
            {isLoading ? (
              <div className="flex flex-col gap-y-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 p-3 rounded-lg border border-neutral-200"
                  >
                    <div className="flex-1 min-w-0">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <div className="flex gap-4">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-5 rounded" />
                  </div>
                ))}
              </div>
            ) : filteredShows.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-secondary-blue">
                  {searchQuery
                    ? "No shows match your search"
                    : "No upcoming shows"}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-y-3">
                {filteredShows.map((show) => (
                  <ShowSelectRow
                    key={show.id}
                    show={show}
                    isSelected={selectedShowIds.has(show.id)}
                    isDisabled={existingShowIds.has(show.id)}
                    onToggle={handleToggle}
                  />
                ))}
                {hasMore && !searchQuery && (
                  <Button
                    variant="ghost"
                    onClick={fetchMoreShows}
                    disabled={isLoadingMore}
                    className="w-full text-sm text-secondary-blue hover:text-primary-blue"
                  >
                    {isLoadingMore ? "Loading..." : "View more shows"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-end border-t border-neutral-100 px-5 py-5 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isAdding}
            className="w-[107px]"
          >
            Cancel
          </Button>
          <PunchupButton
            onClick={handleAdd}
            disabled={selectedCount === 0 || isAdding}
            className="w-[137px]"
          >
            {isAdding ? "Adding..." : "Add"}
          </PunchupButton>
        </div>
      </div>
    </ModalOrDrawer>
  );
}
